import { isMemoryArchiveType } from "@/lib/leaderMemoryVisibility";
import type { EvidenceTimelineItem } from "@/components/coach/sessionPrep/EvidenceTimelineSection";
import type { ThroughlineDayRollup, UseThroughlinePresentationInput } from "@/hooks/useThroughlinePresentation";
import type {
  LeaderLedgerEventItem,
  PracticeSummaryItem,
  TranscriptItem,
} from "@/hooks/useCoachSessionPrepData";
import type { CoachNote } from "@/hooks/useCoachNotes";

function extractPrimaryTopic(value?: string | null) {
  if (!value) return null;
  const normalized = value.replace(/\s+/g, " ").trim();
  if (!normalized) return null;
  const match = normalized.match(/^([A-Za-z][A-Za-z\s-]{2,60})(:|-)\s/);
  if (match?.[1]) return match[1].trim();
  const sentenceMatch = normalized.match(/(.+?[.!?])(\s|$)/);
  const first = sentenceMatch?.[1]?.trim() || normalized;
  return first ? first.slice(0, 60) : null;
}

export function buildCoachSessionPrepThroughline(params: {
  evidenceItems: Array<EvidenceTimelineItem & { subtype: string | null; meeting_id: string | null }>;
  notes: CoachNote[];
  transcripts: TranscriptItem[];
  practiceSummaries: PracticeSummaryItem[];
  ledgerEvents: LeaderLedgerEventItem[];
}): Pick<UseThroughlinePresentationInput, "historyItems" | "recentConversationRecords" | "data"> & {
  latestBehavioralPatternEvents: Array<{
    id: string;
    at: string;
    eventType: string;
    sourceType: string;
    metadata: Record<string, unknown> | null;
  }>;
} {
  const historyItems: UseThroughlinePresentationInput["historyItems"] = params.evidenceItems.map((item) => ({
    id: item.id,
    type:
      item.source === "Transcript"
        ? "transcript"
        : item.subtype === "shared-reflection"
          ? "shared_reflection"
          : item.source === "Reflection"
            ? "reflection"
            : "artifact",
    source: item.source,
    timestamp: item.created_at,
    title: item.title,
    meeting_id: item.meeting_id,
    behavior_name: null,
    transcript_summary: item.source === "Transcript" ? item.detail : null,
    practice_instruction: null,
    rawContent: item.detail,
    rawLink: item.link,
    rawLinkLabel: "Open source",
    artifactPath: null,
  }));

  const recentConversationRecords: UseThroughlinePresentationInput["recentConversationRecords"] = params.notes
    .slice(0, 30)
    .map((note) => ({
      id: note.id,
      source: "Coach message",
      created_at: note.created_at,
      summary: note.content,
    }));

  const transcriptByMeeting = new Map<string, { id: string; created_at: string }>();
  params.transcripts.forEach((transcript) => {
    if (!transcript.meeting_id || transcriptByMeeting.has(transcript.meeting_id)) return;
    transcriptByMeeting.set(transcript.meeting_id, { id: transcript.id, created_at: transcript.created_at });
  });

  const practiceSummarySignals = params.practiceSummaries.map((item) => ({
    id: item.id,
    meeting_id: item.meeting_id,
    meeting_title: item.meeting_title,
    meeting_start_time: item.meeting_start_time,
    behavior_name: extractPrimaryTopic(item.behavior_insights),
    transcript_summary: item.transcript_summary,
    practice_title: item.meeting_title,
    practice_instruction: item.coaching_prompt,
    has_transcript_insights: Boolean(item.transcript_summary),
  }));

  const normalizedLedgerEvents = params.ledgerEvents.filter(event => !isMemoryArchiveType(event.source_type)).map((event) => ({
    id: event.id,
    at: event.created_at,
    eventType: event.event_type,
    sourceType: event.source_type,
    actorRole: event.actor_role,
    confidenceLevel: event.confidence_level,
    metadata: event.metadata,
  }));

  return {
    historyItems,
    recentConversationRecords,
    data: {
      practiceSummarySignals,
      transcriptByMeeting,
      ledgerEvents: normalizedLedgerEvents,
      veeraObservationSignals: [],
      transcriptStats: {
        total: params.transcripts.length,
        analyzed: params.transcripts.filter((item) => item.analysis_status === "analyzed").length,
        withSummary: params.transcripts.filter((item) => Boolean(item.summary)).length,
      },
    },
    latestBehavioralPatternEvents: normalizedLedgerEvents.map((event) => ({
      id: event.id,
      at: event.at,
      eventType: event.eventType,
      sourceType: event.sourceType,
      metadata: event.metadata,
    })),
  };
}

export function buildCoachSessionPrepThroughlineUnsaidSignals(
  selectedThroughlineRollup: ThroughlineDayRollup | null,
): string[] {
  if (!selectedThroughlineRollup) return [];
  const unsaid: string[] = [];
  if (selectedThroughlineRollup.alignment === "gap") {
    unsaid.push("Potential say-vs-do gap: commitments may not yet be reflected in behavior signals.");
  }
  if (selectedThroughlineRollup.evidence < 0.55) {
    unsaid.push("Evidence is thin. More transcript-backed moments are needed before drawing strong conclusions.");
  }
  if (selectedThroughlineRollup.sourceCounts.reflection === 0) {
    unsaid.push("No reflection signal captured for this day. Internal narrative may be missing.");
  }
  if (selectedThroughlineRollup.sourceCounts.transcript === 0) {
    unsaid.push("No transcript signal captured for this day. Observable behavior cues are limited.");
  }
  if (unsaid.length === 0) {
    unsaid.push("No major unsaid gap detected for this day based on current evidence.");
  }
  return unsaid.slice(0, 3);
}
